import React, { useState } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { LucideLightbulb, LucideLayers, LucideCpu, LucideWind } from 'lucide-react';

const FEATURES = [
  { 
    id: 'lighting', 
    icon: <LucideLightbulb size={18} />, 
    title: 'Smart Lighting', 
    desc: 'Adaptive ambiance that mirrors the cycle of the sun.',
    color: '#FFD700',
    trigger: 0.1
  },
  { 
    id: 'curtains', 
    icon: <LucideLayers size={18} />, 
    title: 'Motorized Shades', 
    desc: 'Privacy and natural light, balanced by intelligence.',
    color: '#c8d8c8',
    trigger: 0.2
  },
  { 
    id: 'panel', 
    icon: <LucideCpu size={18} />, 
    title: 'Control Core', 
    desc: 'The centralized brain of your immersive sanctuary.',
    color: '#7ec8a0',
    trigger: 0.3
  },
  { 
    id: 'climate', 
    icon: <LucideWind size={18} />, 
    title: 'Climate Senses', 
    desc: 'Precision thermal comfort, automated for wellness.',
    color: '#00BFFF',
    trigger: 0.4
  },
];

/* ── Single bubble + its hover card ── */
const FeatureIcon = ({ data, index, hoveredId, setHoveredId, scrollProgress }) => {
  const isHovered = hoveredId === data.id;

  const entranceStart = Math.max(0, data.trigger - 0.1);
  const entranceRange = [entranceStart, data.trigger];

  const xEntrance = useTransform(scrollProgress, entranceRange, [80, 0]);
  const opacity   = useTransform(scrollProgress, entranceRange, [0, 1]);
  const scaleIn   = useTransform(scrollProgress, entranceRange, [0.7, 1]);

  return (
    /* Outer wrapper: drives scroll-entrance but keeps overflow visible for the card */
    <motion.div
      style={{ x: xEntrance, opacity, scale: scaleIn }}
      className="relative mb-5 will-change-transform pointer-events-auto"
      /* overflow must NOT be hidden — card pops out to the left */
    >
      {/* Row: [card]  [bubble] — both vertically centered */}
      <div
        className="flex items-center justify-end gap-4"
        onMouseEnter={() => setHoveredId(data.id)}
        onMouseLeave={() => setHoveredId(null)}
      >
        {/* ── Hover card — sits in the flex row to the LEFT of the bubble ── */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              key={data.id}
              initial={{ opacity: 0, x: 12, scale: 0.93, filter: 'blur(8px)' }}
              animate={{ opacity: 1, x: 0,  scale: 1,    filter: 'blur(0px)' }}
              exit={{    opacity: 0, x: 12, scale: 0.93, filter: 'blur(8px)' }}
              transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
              className="w-60 shrink-0 relative"
            >
              {/* Connector dot on the right edge of card */}
              <div
                className="absolute right-[-10px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full z-10"
                style={{
                  background: data.color,
                  boxShadow: `0 0 10px ${data.color}cc`,
                }}
              />

              {/* Glass card */}
              <div
                className="rounded-[1.5rem] overflow-hidden border border-white/10"
                style={{
                  background: 'rgba(12,12,12,0.75)',
                  backdropFilter: 'blur(36px)',
                  WebkitBackdropFilter: 'blur(36px)',
                  boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
                }}
              >
                {/* Top accent line in feature colour */}
                <div
                  className="h-px w-full"
                  style={{ background: `linear-gradient(90deg, ${data.color}99, transparent)` }}
                />

                <div className="p-5">
                  {/* Icon + title */}
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{
                        background: `${data.color}1a`,
                        border: `1px solid ${data.color}35`,
                        color: data.color,
                      }}
                    >
                      {data.icon}
                    </div>
                    <div>
                      <span className="text-[7px] font-mono uppercase tracking-[0.4em] text-white/40 block leading-none mb-1">
                        Automated
                      </span>
                      <h4 className="text-white font-serif text-sm font-medium leading-tight">
                        {data.title}
                      </h4>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-white/50 text-[11px] leading-relaxed font-light">
                    {data.desc}
                  </p>

                  {/* Footer */}
                  <div className="mt-4 pt-3 border-t border-white/[0.05] flex items-center justify-between">
                    <span className="text-[8px] font-mono tracking-widest text-white/20 uppercase">
                      SYS·{String(index + 1).padStart(2, '0')}
                    </span>
                    <span
                      className="text-[8px] font-mono tracking-widest uppercase"
                      style={{ color: `${data.color}80` }}
                    >
                      ● Active
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Floating bubble ── */}
        <motion.div
          whileHover={{ scale: 1.15, rotate: 5 }}
          animate={{ y: [0, -8, 0], rotate: [0, 2, -2, 0] }}
          transition={{
            y:       { duration: 4 + index * 0.6, repeat: Infinity, ease: 'easeInOut' },
            rotate:  { duration: 4 + index * 0.6, repeat: Infinity, ease: 'easeInOut' },
            scale:   { type: 'spring', stiffness: 400, damping: 22 },
            default: { type: 'spring', stiffness: 400, damping: 22 },
          }}
          className={`w-14 h-14 shrink-0 rounded-full flex items-center justify-center cursor-pointer
            overflow-hidden relative
            bg-gradient-to-br from-white/20 to-transparent
            backdrop-blur-md
            transition-all duration-500
            ${isHovered
              ? 'ring-[2.5px] ring-offset-0'
              : 'border border-white/25'
            }
          `}
          style={isHovered ? {
            border: `1.5px solid ${data.color}80`,
            boxShadow: `inset 0 2px 10px rgba(255,255,255,0.3), 0 10px 32px rgba(0,0,0,0.4), 0 0 28px ${data.color}44`,
            '--tw-ring-color': `${data.color}30`,
          } : {
            boxShadow: 'inset 0 2px 10px rgba(255,255,255,0.25), 0 10px 30px rgba(0,0,0,0.3)',
          }}
        >
          {/* Glass sheen */}
          <div className="absolute top-1.5 left-2 w-3 h-1.5 bg-white/40 rounded-full blur-[1px] rotate-[-15deg]" />
          <span
            className="relative z-10 transition-colors duration-300"
            style={{ color: isHovered ? data.color : 'white' }}
          >
            {data.icon}
          </span>
          <div className="absolute inset-0 rounded-full border border-white/5 opacity-20 scale-125 animate-pulse" />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default function AntiGravityHero({ containerRef }) {
  const [hoveredId, setHoveredId] = useState(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const bgBlur  = useTransform(scrollYProgress, [0, 1], ['blur(0px)', 'blur(10px)']);
  const bgY     = useTransform(scrollYProgress, [0, 1], ['0%', '5%']);

  return (
    <div className="w-full h-screen relative bg-[#2d2a26] overflow-hidden select-none">

      {/* ── LAYER 1: BACKGROUND ── */}
      <motion.div
        style={{ scale: bgScale, y: bgY, filter: bgBlur }}
        className="absolute inset-0 z-0 will-change-transform"
      >
        <img
          src="../imgHome/HA-intro-img.jpg"
          alt="Smart Home Interior"
          className="w-full h-full object-cover brightness-[0.35]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2d2a26]/80 via-transparent to-[#24382f]/30" />
      </motion.div>

      {/* ── LAYER 2: CENTERED HEADLINE ── */}
      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <span className="text-[10px] font-mono uppercase tracking-[0.8em] text-white mb-6 block">
            Residential Intelligence
          </span>
          <h1 className="text-6xl md:text-9xl font-serif text-white leading-[0.85]">
            Living <br />
            <span className="italic font-normal text-gold">Intuition.</span>
          </h1>
        </motion.div>
      </div>

      {/* ── LAYER 3: BUBBLE COLUMN (right side, overflow visible so cards show) ── */}
      <div
        className="absolute right-10 md:right-20 top-1/2 -translate-y-1/2 z-20 flex flex-col items-end"
        style={{ overflow: 'visible' }}
      >
        {FEATURES.map((feature, i) => (
          <FeatureIcon
            key={feature.id}
            data={feature}
            index={i}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            scrollProgress={scrollYProgress}
          />
        ))}
      </div>

      {/* Progress Line */}
      <div className="absolute right-5 top-1/4 bottom-1/4 w-px bg-white/5 z-5 overflow-hidden">
        <motion.div
          className="w-full bg-gold/40 origin-top"
          style={{ scaleY: scrollYProgress }}
        />
      </div>

      {/* Hover global backdrop */}
      <AnimatePresence>
        {hoveredId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[5] pointer-events-none bg-black/20 backdrop-blur-[2px]"
          />
        )}
      </AnimatePresence>
    </div>
  );
}
